import React, { useEffect, useState } from "react";
import { api, BACKEND_URL } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Download } from "lucide-react";

const GROUPS = [
  { v: "product", l: "By product" }, { v: "supplier", l: "By supplier" },
  { v: "airline", l: "By airline" }, { v: "user", l: "By user" },
  { v: "country", l: "By origin country/airport" }, { v: "date", l: "By date" },
];

export default function AdminReports() {
  const [params, setParams] = useState({ start: "", end: "", group_by: "product" });
  const [data, setData] = useState(null);
  const load = async () => {
    const q = new URLSearchParams();
    if (params.start) q.set("start", params.start);
    if (params.end) q.set("end", params.end);
    q.set("group_by", params.group_by);
    const { data } = await api.get(`/admin/reports/sales?${q.toString()}`);
    setData(data);
  };
  useEffect(() => { load(); }, [params.group_by]); // eslint-disable-line

  const downloadExcel = async () => {
    const token = localStorage.getItem("fa_admin_token");
    const q = new URLSearchParams();
    if (params.start) q.set("start", params.start);
    if (params.end) q.set("end", params.end);
    const url = `${BACKEND_URL}/api/admin/reports/sales/export?${q.toString()}`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return alert("Download failed");
    const blob = await res.blob();
    const u = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = u; a.download = `sales-report.xlsx`; a.click();
    URL.revokeObjectURL(u);
  };

  return (
    <div>
      <h1 className="font-heading text-3xl font-semibold text-[var(--brand-accent)]">Sales reports</h1>
      <p className="text-slate-500 mt-1">Pivot revenue and download as Excel.</p>

      <Card className="mt-6 p-5 grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
        <div><Label>From</Label><Input data-testid="rep-start" type="date" value={params.start} onChange={e=>setParams({...params,start:e.target.value})}/></div>
        <div><Label>To</Label><Input data-testid="rep-end" type="date" value={params.end} onChange={e=>setParams({...params,end:e.target.value})}/></div>
        <div className="md:col-span-2">
          <Label>Group by</Label>
          <Select value={params.group_by} onValueChange={v=>setParams({...params,group_by:v})}>
            <SelectTrigger data-testid="rep-group"><SelectValue /></SelectTrigger>
            <SelectContent>{GROUPS.map(g => <SelectItem key={g.v} value={g.v}>{g.l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button data-testid="rep-run" onClick={load} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] rounded-full">Run</Button>
        <Button data-testid="rep-excel" onClick={downloadExcel} variant="outline" className="rounded-full"><Download className="w-4 h-4 mr-1" /> Excel</Button>
      </Card>

      {data && (
        <>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="p-5"><div className="text-xs uppercase tracking-wider text-slate-400 font-bold">Total revenue</div><div className="font-heading text-3xl font-bold text-[var(--brand-accent)] mt-1">${Number(data.total_revenue).toLocaleString()}</div></Card>
            <Card className="p-5"><div className="text-xs uppercase tracking-wider text-slate-400 font-bold">Paid transactions</div><div className="font-heading text-3xl font-bold text-[var(--brand-accent)] mt-1">{data.total_txns}</div></Card>
            <Card className="p-5"><div className="text-xs uppercase tracking-wider text-slate-400 font-bold">Group by</div><div className="font-heading text-3xl font-bold text-[var(--brand-accent)] mt-1 capitalize">{data.group_by}</div></Card>
          </div>
          <Card className="mt-5 overflow-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Key</TableHead><TableHead>Transactions</TableHead><TableHead>Revenue</TableHead></TableRow></TableHeader>
              <TableBody>
                {data.series.map((s,i) => (
                  <TableRow key={i} data-testid={`rep-row-${i}`}><TableCell className="font-medium">{s.key}</TableCell><TableCell>{s.count}</TableCell><TableCell className="font-semibold">${Number(s.revenue).toLocaleString()}</TableCell></TableRow>
                ))}
                {data.series.length === 0 && <TableRow><TableCell colSpan={3} className="text-center text-slate-400 py-10">No paid transactions in this range</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Card>
        </>
      )}
    </div>
  );
}
